import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useRaceResultsDays } from "@/hooks/useRaceResultsDays";
import { cn } from "@/lib/utils";
import {
  VIEWS,
  byStartTime,
  daysEndingAt,
  earliestResultsIso,
  filterRaces,
  formatDayShort,
  latestResultsIso,
  monthDays,
  sydneyTodayIso,
  sydneyZoneLabel,
} from "@/utils/race-results";
import {
  CalendarDays,
  CircleDot,
  List,
  SlidersHorizontal,
  Trophy,
} from "lucide-react";
import PageMeta from "@/components/PageMeta";
import Navbar from "@/components/navbar";
import MeetingsView from "@/components/race-results/MeetingsView";
import ResultsCalendar from "@/components/race-results/ResultsCalendar";
import ResultsList from "@/components/race-results/ResultsList";
import WinnersGrid from "@/components/race-results/WinnersGrid";
import { PillLegend, ProBand } from "@/components/race-results/parts";
import Footer from "@/pages/landing/footer";

const VIEW_STORAGE_KEY = "lexi-race-results-view";
const VIEW_TABS = [
  { key: "list", label: "List", Icon: List },
  { key: "winners", label: "Winners", Icon: Trophy },
  { key: "meetings", label: "Meetings", Icon: CircleDot },
  { key: "calendar", label: "Calendar", Icon: CalendarDays },
];

const FIELD = "grid min-w-0 gap-1";
const FIELD_LABEL =
  "text-[11px] font-bold uppercase tracking-wider text-gray-500";
const CONTROL =
  "h-[38px] min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 text-sm focus:border-brand-pink focus:outline-none focus:ring-2 focus:ring-[#FFE6F6]";

function readStoredView() {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return VIEWS.includes(v) ? v : null;
  } catch {
    return null;
  }
}

export default function RaceResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const todayIso = useMemo(() => sydneyTodayIso(), []);
  const latestIso = useMemo(() => latestResultsIso(), []);
  const earliestIso = useMemo(() => earliestResultsIso(), []);

  const paramView = searchParams.get("view");
  const [view, setView] = useState(() =>
    VIEWS.includes(paramView) ? paramView : readStoredView() || "list",
  );
  useEffect(() => {
    if (VIEWS.includes(paramView) && paramView !== view) setView(paramView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramView]);

  const [query, setQuery] = useState("");
  const [range, setRange] = useState("7");
  const [pickedDate, setPickedDate] = useState(latestIso);
  const [state, setState] = useState("all");
  const [track, setTrack] = useState("all");
  const [show, setShow] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [calMonth, setCalMonth] = useState(() => ({
    year: Number(latestIso.slice(0, 4)),
    month: Number(latestIso.slice(5, 7)),
  }));
  const [calDay, setCalDay] = useState(latestIso);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const chooseView = (next) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // storage can be blocked; the URL still carries the view
    }
    const params = new URLSearchParams(searchParams);
    if (next === "list") params.delete("view");
    else params.set("view", next);
    setSearchParams(params, { replace: true });
  };

  const isCalendar = view === "calendar";
  const dates = useMemo(() => {
    if (isCalendar)
      return monthDays(calMonth.year, calMonth.month, latestIso, earliestIso);
    if (range === "yesterday") return [latestIso];
    if (range === "30") return daysEndingAt(latestIso, 30);
    if (range === "date") return [pickedDate];
    return daysEndingAt(latestIso, 7);
  }, [isCalendar, calMonth, range, pickedDate, latestIso, earliestIso]);

  const rawDays = useRaceResultsDays(dates);

  const { states, tracks } = useMemo(() => {
    const all = rawDays.flatMap((d) => d.races);
    return {
      states: [...new Set(all.map((r) => r.state).filter(Boolean))].sort(),
      tracks: [
        ...new Set(
          all
            .filter((r) => state === "all" || r.state === state)
            .map((r) => r.track),
        ),
      ].sort(),
    };
  }, [rawDays, state]);

  const days = useMemo(
    () =>
      rawDays.map((d) => ({
        ...d,
        races: filterRaces(d.races, {
          query,
          state,
          track,
          show: view === "list" ? show : "all",
        }).sort(byStartTime),
      })),
    [rawDays, query, state, track, show, view],
  );

  const activeFilters =
    (!isCalendar && range !== "7") +
    (state !== "all") +
    (track !== "all") +
    (view === "list" && show !== "all");

  const monthStep = (delta) => {
    const d = new Date(Date.UTC(calMonth.year, calMonth.month - 1 + delta, 1));
    const next = { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
    setCalMonth(next);
    const inMonth = monthDays(next.year, next.month, latestIso, earliestIso);
    setCalDay(inMonth[0] || null);
  };
  const monthKey = (iso) => iso.slice(0, 7);
  const currentKey = `${calMonth.year}-${String(calMonth.month).padStart(2, "0")}`;

  return (
    <>
      <PageMeta
        title="Race Results"
        description="Every race Lexi tipped, with the winner and how Lexi's Early Mail, Race Day and Late Mail tips went. Updated every morning."
        canonicalUrl="/race-results"
      />
      <Navbar />
      <main className="mx-auto grid max-w-[1180px] gap-4 px-4 pb-14 pt-[118px] md:gap-5 md:px-6">
        <h1 className="text-[26px] font-extrabold tracking-tight text-gray-950 md:text-4xl">
          Race results
        </h1>

        <div className="flex flex-wrap items-end gap-2.5 rounded-xl border border-gray-200 bg-white p-3">
          <label
            className={cn(FIELD, "flex-[1_1_180px]")}
            htmlFor="race-results-search"
          >
            <span className={FIELD_LABEL}>Search</span>
            <input
              id="race-results-search"
              type="search"
              placeholder="Horse or track"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={cn(CONTROL, "w-full")}
            />
          </label>
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            className="inline-flex h-[38px] items-center gap-1.5 rounded-lg border border-gray-200 px-3.5 text-sm font-semibold md:hidden"
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
            Filters
            {activeFilters > 0 && (
              <span className="rounded-full bg-brand-pink px-1.5 text-[11px] text-white">
                {activeFilters}
              </span>
            )}
          </button>
          <div
            className={cn(
              "w-full flex-wrap gap-2.5 md:flex md:w-auto",
              filtersOpen ? "grid" : "hidden",
            )}
          >
            {!isCalendar && (
              <label className={FIELD} htmlFor="race-results-range">
                <span className={FIELD_LABEL}>Date</span>
                <select
                  id="race-results-range"
                  className={CONTROL}
                  value={range}
                  onChange={(e) => setRange(e.target.value)}
                >
                  <option value="yesterday">
                    Yesterday, {formatDayShort(latestIso)}
                  </option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="date">Pick a date</option>
                </select>
              </label>
            )}
            {!isCalendar && range === "date" && (
              <label className={FIELD} htmlFor="race-results-date">
                <span className={FIELD_LABEL}>Day</span>
                <input
                  id="race-results-date"
                  type="date"
                  className={CONTROL}
                  min={earliestIso}
                  max={latestIso}
                  value={pickedDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && v >= earliestIso && v <= latestIso)
                      setPickedDate(v);
                  }}
                />
              </label>
            )}
            <label className={FIELD} htmlFor="race-results-state">
              <span className={FIELD_LABEL}>State</span>
              <select
                id="race-results-state"
                className={CONTROL}
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  setTrack("all");
                }}
              >
                <option value="all">All states</option>
                {states.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className={FIELD} htmlFor="race-results-track">
              <span className={FIELD_LABEL}>Track</span>
              <select
                id="race-results-track"
                className={CONTROL}
                value={track}
                onChange={(e) => setTrack(e.target.value)}
              >
                <option value="all">All tracks</option>
                {tracks.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            {view === "list" && (
              <label className={FIELD} htmlFor="race-results-show">
                <span className={FIELD_LABEL}>Show</span>
                <select
                  id="race-results-show"
                  className={CONTROL}
                  value={show}
                  onChange={(e) => setShow(e.target.value)}
                >
                  <option value="all">All races</option>
                  <option value="top">A Top Tip won</option>
                  <option value="any">Lexi tipped the winner</option>
                </select>
              </label>
            )}
          </div>
        </div>

        <div
          className="grid grid-cols-4 border-b border-gray-200 sm:flex sm:gap-0.5"
          role="group"
          aria-label="View"
        >
          {VIEW_TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => chooseView(key)}
              aria-pressed={view === key}
              className={cn(
                "-mb-px inline-flex flex-col items-center gap-1 border-b-2 px-1 py-2 text-xs font-semibold sm:flex-row sm:gap-1.5 sm:px-3 sm:pb-2.5 sm:text-sm",
                view === key
                  ? "border-brand-pink text-[#C8007E]"
                  : "border-transparent text-gray-600 hover:text-gray-900",
              )}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>

        {(view === "list" || view === "calendar") && (
          <PillLegend zone={sydneyZoneLabel(latestIso)} />
        )}

        {view === "list" && <ResultsList days={days} latestIso={latestIso} />}
        {view === "winners" && <WinnersGrid days={days} />}
        {view === "meetings" && <MeetingsView days={days} />}
        {view === "calendar" && (
          <ResultsCalendar
            year={calMonth.year}
            month={calMonth.month}
            days={days}
            selectedIso={calDay}
            onSelect={setCalDay}
            onPrev={() => monthStep(-1)}
            onNext={() => monthStep(1)}
            canPrev={currentKey > monthKey(earliestIso)}
            canNext={currentKey < monthKey(latestIso)}
            todayIso={todayIso}
          />
        )}

        <ProBand />
      </main>
      <Footer />
    </>
  );
}
